/**
 * Offline Actions Queue Manager
 *
 * Manages queuing of offline actions (status updates, comments, services, file uploads)
 * Handles FIFO processing and sync when network reconnects
 */

// import { openDB } from "./indexedDB";

// Queue store name
const QUEUE_STORE = "offlineQueue";

// Action types
export type QueueActionType =
  | "STATUS_UPDATE"
  | "ADD_COMMENT"
  | "DELETE_COMMENT"
  | "ADD_SERVICE"
  | "UPLOAD_ATTACHMENT"
  | "DELETE_ATTACHMENT";

export interface QueuedAction {
  id: string;
  type: QueueActionType;
  payload: any;
  shipmentId: string;
  timestamp: number;
  retries: number;
  maxRetries: number;
  status: "pending" | "processing" | "failed" | "success";
  error?: string;
}

/**
 * Initialize queue database
 */
const initQueueDB = async (): Promise<IDBDatabase> => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open("livecargo-queue", 1);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;

      if (!db.objectStoreNames.contains(QUEUE_STORE)) {
        const store = db.createObjectStore(QUEUE_STORE, { keyPath: "id" });
        store.createIndex("timestamp", "timestamp", { unique: false });
        store.createIndex("status", "status", { unique: false });
        store.createIndex("type", "type", { unique: false });
        store.createIndex("shipmentId", "shipmentId", { unique: false });
        // console.log("[Queue] Created offline queue store");
      }
    };
  });
};

/**
 * Generate unique ID for queued action
 */
const generateId = (): string => {
  return `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Add action to queue
 */
export const enqueueAction = async (
  type: QueueActionType,
  payload: any,
  shipmentId: string,
  maxRetries: number = 3,
): Promise<string> => {
  const db = await initQueueDB();

  const action: QueuedAction = {
    id: generateId(),
    type,
    payload,
    shipmentId,
    timestamp: Date.now(),
    retries: 0,
    maxRetries,
    status: "pending",
  };

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([QUEUE_STORE], "readwrite");
    const store = transaction.objectStore(QUEUE_STORE);
    const request = store.add(action);

    request.onsuccess = () => {
      // console.log("[Queue] Enqueued action:", type, action.id);
      resolve(action.id);
    };

    request.onerror = () => {
      console.error("[Queue] Failed to enqueue action:", request.error);
      reject(request.error);
    };
  });
};

/**
 * Get all pending actions (FIFO order)
 */
export const getPendingActions = async (): Promise<QueuedAction[]> => {
  const db = await initQueueDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([QUEUE_STORE], "readonly");
    const store = transaction.objectStore(QUEUE_STORE);
    const index = store.index("timestamp");
    const request = index.getAll();

    request.onsuccess = () => {
      const actions = (request.result || []).filter(
        (action: QueuedAction) => action.status === "pending",
      );
      // Sort by timestamp (FIFO)
      actions.sort((a, b) => a.timestamp - b.timestamp);
      resolve(actions);
    };

    request.onerror = () => reject(request.error);
  });
};

/**
 * Update action status
 */
export const updateActionStatus = async (
  id: string,
  status: QueuedAction["status"],
  error?: string,
): Promise<void> => {
  const db = await initQueueDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([QUEUE_STORE], "readwrite");
    const store = transaction.objectStore(QUEUE_STORE);
    const getRequest = store.get(id);

    getRequest.onsuccess = () => {
      const action = getRequest.result;
      if (!action) {
        reject(new Error("Action not found"));
        return;
      }

      action.status = status;
      if (error) action.error = error;
      if (status === "processing") action.retries += 1;

      const putRequest = store.put(action);
      putRequest.onsuccess = () => resolve();
      putRequest.onerror = () => reject(putRequest.error);
    };

    getRequest.onerror = () => reject(getRequest.error);
  });
};

/**
 * Remove action from queue
 */
export const removeAction = async (id: string): Promise<void> => {
  const db = await initQueueDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([QUEUE_STORE], "readwrite");
    const store = transaction.objectStore(QUEUE_STORE);
    const request = store.delete(id);

    request.onsuccess = () => {
      // console.log("[Queue] Removed action:", id);
      resolve();
    };

    request.onerror = () => reject(request.error);
  });
};

/**
 * Get queue statistics
 */
export const getQueueStats = async (): Promise<{
  pending: number;
  processing: number;
  failed: number;
  total: number;
}> => {
  const db = await initQueueDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([QUEUE_STORE], "readonly");
    const store = transaction.objectStore(QUEUE_STORE);
    const request = store.getAll();

    request.onsuccess = () => {
      const actions = request.result || [];
      resolve({
        pending: actions.filter((a: QueuedAction) => a.status === "pending")
          .length,
        processing: actions.filter(
          (a: QueuedAction) => a.status === "processing",
        ).length,
        failed: actions.filter((a: QueuedAction) => a.status === "failed")
          .length,
        total: actions.length,
      });
    };

    request.onerror = () => reject(request.error);
  });
};

/**
 * Clear all completed actions
 */
export const clearCompletedActions = async (): Promise<void> => {
  const db = await initQueueDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([QUEUE_STORE], "readwrite");
    const store = transaction.objectStore(QUEUE_STORE);
    const request = store.getAll();

    request.onsuccess = () => {
      const actions = request.result || [];
      const toDelete = actions.filter(
        (a: QueuedAction) => a.status === "success",
      );

      toDelete.forEach((action: QueuedAction) => {
        store.delete(action.id);
      });

      transaction.oncomplete = () => {
        // console.log("[Queue] Cleared completed actions:", toDelete.length);
        resolve();
      };
    };

    request.onerror = () => reject(request.error);
  });
};

/**
 * Clear all actions (use with caution)
 */
export const clearAllActions = async (): Promise<void> => {
  const db = await initQueueDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([QUEUE_STORE], "readwrite");
    const store = transaction.objectStore(QUEUE_STORE);
    const request = store.clear();

    request.onsuccess = () => {
      // console.log("[Queue] Cleared all actions");
      resolve();
    };

    request.onerror = () => reject(request.error);
  });
};

/**
 * Get actions for specific shipment
 */
export const getShipmentActions = async (
  shipmentId: string,
): Promise<QueuedAction[]> => {
  const db = await initQueueDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([QUEUE_STORE], "readonly");
    const store = transaction.objectStore(QUEUE_STORE);
    const index = store.index("shipmentId");
    const request = index.getAll(shipmentId);

    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => reject(request.error);
  });
};

/**
 * Check if action should be retried
 */
export const shouldRetry = (action: QueuedAction): boolean => {
  return action.retries < action.maxRetries;
};
